import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, CheckCircle, AlertCircle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const inputCls = `pl-10 !bg-[#111] border-white/20 text-white placeholder:text-white/30 focus:border-${isCyber ? "cyan-400" : "divine-gold"} focus:ring-0`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to reset password. The link may have expired.";
      setError(detail);
      toast.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="glass rounded-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Invalid Reset Link</h2>
          <p className="text-white/60 text-sm mb-6">This password reset link is invalid or has expired.</p>
          <Button onClick={() => navigate("/auth")} className={isCyber ? "bg-cyan-500 hover:bg-cyan-600 text-black" : "bg-divine-gold hover:bg-divine-gold/90 text-black"}>
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <Toaster position="top-center" richColors />
        <div className="glass rounded-xl p-8 max-w-md w-full text-center">
          <CheckCircle className={`w-12 h-12 mx-auto mb-4 ${isCyber ? "text-cyan-400" : "text-green-400"}`} />
          <h2 className="text-white text-xl font-semibold mb-2">Password Reset Complete</h2>
          <p className="text-white/60 text-sm mb-6">Your password has been updated. You can now log in with your new password.</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate("/profile?tab=settings")}
              variant="outline"
              className={`${isCyber ? "border-cyan-400/30 text-cyan-400 hover:bg-cyan-400/10" : "border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"}`}
              data-testid="reset-back-to-profile-btn"
            >
              Back to Profile
            </Button>
            <Button
              onClick={() => navigate("/auth")}
              className={isCyber ? "bg-cyan-500 hover:bg-cyan-600 text-black" : "bg-divine-gold hover:bg-divine-gold/90 text-black"}
              data-testid="reset-back-to-login-btn"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <Toaster position="top-center" richColors />
      <div className="glass rounded-xl p-8 max-w-md w-full">
        <h2 className={`text-xl font-semibold mb-1 ${isCyber ? "text-cyan-400" : "font-cinzel text-divine-gold"}`}>
          Set New Password
        </h2>
        <p className="text-white/50 text-sm mb-6">Enter your new password below.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-white/80 text-sm">New Password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className={inputCls}
                required
                data-testid="reset-new-password"
              />
            </div>
          </div>
          <div>
            <Label className="text-white/80 text-sm">Confirm Password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                className={inputCls}
                required
                data-testid="reset-confirm-password"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={isSubmitting || !password || !confirmPassword}
            className={`w-full ${isCyber ? "bg-cyan-500 hover:bg-cyan-600 text-black" : "bg-divine-gold hover:bg-divine-gold/90 text-black"}`}
            data-testid="reset-submit-btn"
          >
            {isSubmitting ? "Resetting..." : "Reset Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
